import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Tabs,
  Table,
  Button,
  Tag,
  Space,
  Input,
  Select,
  Modal,
  Form,
  InputNumber,
  Switch,
  message,
  Tooltip,
  Badge,
  Statistic,
  Row,
  Col,
  Progress,
  Upload,
  Typography,
  Popconfirm,
  Timeline,
  Empty,
  Alert,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  UploadOutlined,
  ReloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  QuestionCircleOutlined,
  LinkOutlined,
  HistoryOutlined,
  SettingOutlined,
  RobotOutlined,
  BranchesOutlined,
} from '@ant-design/icons';
import { vendorMappingService, vendorService } from '../services/api';

const { TabPane } = Tabs;
const { Search } = Input;
const { Text, Title } = Typography;

const VendorMapping = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  // Pending queue state
  const [pendingItems, setPendingItems] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [pendingPage, setPendingPage] = useState(1);
  const [selectedPending, setSelectedPending] = useState([]);
  const [approvalModal, setApprovalModal] = useState({ visible: false, item: null });
  const [vendors, setVendors] = useState([]);

  // Global aliases state
  const [aliases, setAliases] = useState([]);
  const [aliasSearch, setAliasSearch] = useState('');
  const [aliasModal, setAliasModal] = useState({ visible: false, alias: null });

  // Settings state
  const [settings, setSettings] = useState(null);
  const [settingsForm] = Form.useForm();

  // ML Stats state
  const [mlStats, setMlStats] = useState(null);

  // Load initial data
  useEffect(() => {
    loadStats();
    loadVendors();
  }, []);

  useEffect(() => {
    if (activeTab === 'pending') {
      loadPendingQueue();
    } else if (activeTab === 'aliases') {
      loadAliases();
    } else if (activeTab === 'settings') {
      loadSettings();
      loadMlStats();
    }
  }, [activeTab, pendingPage]);

  const loadStats = async () => {
    try {
      const data = await vendorMappingService.getPendingStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadVendors = async () => {
    try {
      const data = await vendorService.getAll();
      setVendors(data);
    } catch (error) {
      console.error('Failed to load vendors:', error);
    }
  };

  const loadPendingQueue = async () => {
    setLoading(true);
    try {
      const data = await vendorMappingService.getPendingQueue({
        status: 'pending',
        skip: (pendingPage - 1) * 20,
        limit: 20,
        sort_by: 'total_spend',
        sort_order: 'desc',
      });
      setPendingItems(data.items || []);
      setPendingTotal(data.total || 0);
    } catch (error) {
      message.error('Failed to load pending queue');
    } finally {
      setLoading(false);
    }
  };

  const loadAliases = async () => {
    setLoading(true);
    try {
      const data = await vendorMappingService.getGlobalAliases({
        search: aliasSearch || undefined,
        limit: 100,
      });
      setAliases(data);
    } catch (error) {
      message.error('Failed to load aliases');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const data = await vendorMappingService.getSettings();
      setSettings(data);
      settingsForm.setFieldsValue(data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadMlStats = async () => {
    try {
      const data = await vendorMappingService.getMLStats();
      setMlStats(data);
    } catch (error) {
      console.error('Failed to load ML stats:', error);
    }
  };

  // Pending queue handlers
  const handleApprovePending = async (values) => {
    try {
      await vendorMappingService.approvePending(
        approvalModal.item.id,
        values.vendor_id,
        values.create_global_alias,
        values.notes
      );
      message.success('Vendor match approved');
      setApprovalModal({ visible: false, item: null });
      loadPendingQueue();
      loadStats();
    } catch (error) {
      message.error('Failed to approve match');
    }
  };

  const handleRejectPending = async (id, reason) => {
    try {
      await vendorMappingService.rejectPending(id, reason);
      message.success('Match rejected');
      loadPendingQueue();
      loadStats();
    } catch (error) {
      message.error('Failed to reject match');
    }
  };

  const handleBulkApprove = async () => {
    try {
      const result = await vendorMappingService.bulkApprovePending(selectedPending, true);
      message.success(`Approved ${result.approved?.length || 0} items`);
      setSelectedPending([]);
      loadPendingQueue();
      loadStats();
    } catch (error) {
      message.error('Bulk approval failed');
    }
  };

  const handleAutoResolve = async (dryRun = true) => {
    try {
      const result = await vendorMappingService.autoResolvePending(95, dryRun);
      if (dryRun) {
        Modal.confirm({
          title: 'Auto-Resolve Preview',
          content: `This will resolve ${result.would_resolve || 0} high-confidence matches. Proceed?`,
          onOk: () => handleAutoResolve(false),
        });
      } else {
        message.success(`Resolved ${result.resolved || 0} items`);
        loadPendingQueue();
        loadStats();
      }
    } catch (error) {
      message.error('Auto-resolve failed');
    }
  };

  // Alias handlers
  const handleCreateAlias = async (values) => {
    try {
      await vendorMappingService.createGlobalAlias(values);
      message.success('Alias created');
      setAliasModal({ visible: false, alias: null });
      loadAliases();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to create alias');
    }
  };

  const handleUpdateAlias = async (values) => {
    try {
      await vendorMappingService.updateGlobalAlias(aliasModal.alias.id, values);
      message.success('Alias updated');
      setAliasModal({ visible: false, alias: null });
      loadAliases();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to update alias');
    }
  };

  const handleAliasSubmit = async (values) => {
    if (aliasModal.alias) {
      await handleUpdateAlias(values);
    } else {
      await handleCreateAlias(values);
    }
  };

  const handleDeleteAlias = async (id) => {
    try {
      await vendorMappingService.deleteGlobalAlias(id);
      message.success('Alias deleted');
      loadAliases();
    } catch (error) {
      message.error('Failed to delete alias');
    }
  };

  const handleBulkUpload = async (file) => {
    try {
      const result = await vendorMappingService.bulkUploadAliases(file);
      message.success(`Created ${result.created_count} aliases`);
      loadAliases();
    } catch (error) {
      message.error('Bulk upload failed');
    }
    return false; // Prevent default upload
  };

  // Settings handlers
  const handleSaveSettings = async (values) => {
    try {
      await vendorMappingService.updateSettings(values);
      message.success('Settings saved');
      loadSettings();
    } catch (error) {
      message.error('Failed to save settings');
    }
  };

  const handleTrainModel = async () => {
    try {
      const result = await vendorMappingService.trainMLModel();
      if (result.status === 'success') {
        message.success('Model training complete');
      } else {
        message.warning(result.message || 'Not enough data for training');
      }
      loadMlStats();
    } catch (error) {
      message.error('Model training failed');
    }
  };

  // Pending queue columns
  const pendingColumns = [
    {
      title: 'Original Name',
      dataIndex: 'original_name',
      key: 'original_name',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Occurrences',
      dataIndex: 'occurrence_count',
      key: 'occurrence_count',
      sorter: true,
      width: 100,
    },
    {
      title: 'Total Spend',
      dataIndex: 'total_spend',
      key: 'total_spend',
      sorter: true,
      render: (val) => `$${(val || 0).toLocaleString()}`,
      width: 120,
    },
    {
      title: 'Top Suggestion',
      key: 'suggestion',
      render: (_, record) => {
        const suggestion = record.suggestions?.[0];
        if (!suggestion) return <Tag>No suggestions</Tag>;
        return (
          <Space direction="vertical" size={0}>
            <Text>{suggestion.vendor?.name || `Vendor #${suggestion.vendor_id}`}</Text>
            <Space>
              <Tag color={suggestion.confidence === 'high' ? 'green' : suggestion.confidence === 'medium' ? 'orange' : 'red'}>
                {suggestion.combined_score?.toFixed(1)}%
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>{suggestion.confidence}</Text>
            </Space>
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'pending' ? 'processing' : status === 'approved' ? 'success' : 'default'}>
          {status}
        </Tag>
      ),
      width: 100,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => setApprovalModal({ visible: true, item: record })}
          >
            Approve
          </Button>
          <Popconfirm
            title="Reject this match?"
            onConfirm={() => handleRejectPending(record.id)}
          >
            <Button size="small" danger icon={<CloseCircleOutlined />}>
              Reject
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Alias columns
  const aliasColumns = [
    {
      title: 'Alias Name',
      dataIndex: 'alias_name',
      key: 'alias_name',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Vendor',
      key: 'vendor',
      render: (_, record) => {
        const vendor = vendors.find(v => v.id === record.vendor_id);
        return vendor?.name || `Vendor #${record.vendor_id}`;
      },
    },
    {
      title: 'Type',
      dataIndex: 'alias_type',
      key: 'alias_type',
      render: (type) => <Tag>{type || 'general'}</Tag>,
      width: 120,
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>{status}</Tag>
      ),
      width: 100,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => setAliasModal({ visible: true, alias: record })}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this alias?"
            onConfirm={() => handleDeleteAlias(record.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>
        <BranchesOutlined /> Vendor Name Management
      </Title>
      <Text type="secondary" style={{ marginBottom: 24, display: 'block' }}>
        Manage vendor name mappings, aliases, and review unmatched imports. Global aliases benefit all organizations.
      </Text>

      {/* Stats Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Pending Review"
              value={stats?.pending_count || 0}
              prefix={<QuestionCircleOutlined />}
              valueStyle={{ color: stats?.pending_count > 0 ? '#faad14' : '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="High Confidence"
              value={stats?.high_confidence_count || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
              suffix={
                <Tooltip title="Could be auto-approved">
                  <RobotOutlined />
                </Tooltip>
              }
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Pending Spend"
              value={stats?.total_pending_spend || 0}
              prefix="$"
              precision={0}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Approved"
              value={stats?.approved_count || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {/* Pending Review Tab */}
          <TabPane
            tab={
              <span>
                <Badge count={stats?.pending_count || 0} offset={[10, 0]}>
                  Pending Review
                </Badge>
              </span>
            }
            key="pending"
          >
            <Space style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                disabled={selectedPending.length === 0}
                onClick={handleBulkApprove}
              >
                Bulk Approve ({selectedPending.length})
              </Button>
              <Button
                icon={<RobotOutlined />}
                onClick={() => handleAutoResolve(true)}
              >
                Auto-Resolve High Confidence
              </Button>
              <Button icon={<ReloadOutlined />} onClick={loadPendingQueue}>
                Refresh
              </Button>
            </Space>

            <Table
              columns={pendingColumns}
              dataSource={pendingItems}
              rowKey="id"
              loading={loading}
              rowSelection={{
                selectedRowKeys: selectedPending,
                onChange: setSelectedPending,
              }}
              pagination={{
                current: pendingPage,
                pageSize: 20,
                total: pendingTotal,
                onChange: setPendingPage,
              }}
            />
          </TabPane>

          {/* Global Aliases Tab */}
          <TabPane
            tab={
              <span>
                <LinkOutlined /> Global Aliases
              </span>
            }
            key="aliases"
          >
            <Space style={{ marginBottom: 16 }}>
              <Search
                placeholder="Search aliases..."
                value={aliasSearch}
                onChange={(e) => setAliasSearch(e.target.value)}
                onSearch={loadAliases}
                style={{ width: 300 }}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setAliasModal({ visible: true, alias: null })}
              >
                Add Alias
              </Button>
              <Upload
                beforeUpload={handleBulkUpload}
                showUploadList={false}
                accept=".csv"
              >
                <Button icon={<UploadOutlined />}>Bulk Upload CSV</Button>
              </Upload>
            </Space>

            <Alert
              message="Global aliases are shared across all organizations"
              description="When you create an alias here, it will help match vendor names for all users in the system. This creates a network effect where everyone benefits from approved mappings."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Table
              columns={aliasColumns}
              dataSource={aliases}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 20 }}
            />
          </TabPane>

          {/* Settings Tab */}
          <TabPane
            tab={
              <span>
                <SettingOutlined /> Settings
              </span>
            }
            key="settings"
          >
            <Row gutter={24}>
              <Col span={12}>
                <Card title="Matching Thresholds">
                  {settings && (
                    <Form
                      form={settingsForm}
                      layout="vertical"
                      onFinish={handleSaveSettings}
                      initialValues={settings}
                    >
                      <Form.Item
                        name="auto_match_threshold"
                        label={
                          <span>
                            Auto-Match Threshold
                            <Tooltip title="Matches above this score are automatically approved">
                              <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                            </Tooltip>
                          </span>
                        }
                      >
                        <InputNumber min={80} max={100} addonAfter="%" />
                      </Form.Item>

                      <Form.Item
                        name="suggest_threshold"
                        label={
                          <span>
                            Suggestion Threshold
                            <Tooltip title="Matches above this score are suggested to reviewers">
                              <QuestionCircleOutlined style={{ marginLeft: 8 }} />
                            </Tooltip>
                          </span>
                        }
                      >
                        <InputNumber min={50} max={95} addonAfter="%" />
                      </Form.Item>

                      <Form.Item
                        name="min_confidence_threshold"
                        label="Minimum Confidence Threshold"
                      >
                        <InputNumber min={30} max={80} addonAfter="%" />
                      </Form.Item>

                      <Form.Item
                        name="ml_enabled"
                        label="ML-Based Matching"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>

                      <Form.Item
                        name="auto_create_global_alias"
                        label="Auto-Create Global Aliases"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>

                      <Form.Item>
                        <Button type="primary" htmlType="submit">
                          Save Settings
                        </Button>
                      </Form.Item>
                    </Form>
                  )}
                </Card>
              </Col>

              <Col span={12}>
                <Card title="ML Model Statistics">
                  {mlStats ? (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Statistic
                        title="Total Training Decisions"
                        value={mlStats.total_decisions || 0}
                      />

                      {mlStats.accuracy_estimate && (
                        <div>
                          <Text>Model Accuracy</Text>
                          <Progress
                            percent={Math.round(mlStats.accuracy_estimate * 100)}
                            status={mlStats.accuracy_estimate > 0.8 ? 'success' : 'normal'}
                          />
                        </div>
                      )}

                      {mlStats.model_info ? (
                        <Space direction="vertical">
                          <Text type="secondary">
                            Last trained: {mlStats.model_info.trained_at}
                          </Text>
                          <Text type="secondary">
                            Sample size: {mlStats.model_info.sample_size}
                          </Text>
                        </Space>
                      ) : (
                        <Alert
                          message="Model not yet trained"
                          description="Need at least 10 approved decisions to train the model."
                          type="warning"
                        />
                      )}

                      <Button
                        type="primary"
                        icon={<RobotOutlined />}
                        onClick={handleTrainModel}
                      >
                        Train Model
                      </Button>
                    </Space>
                  ) : (
                    <Empty description="Loading ML stats..." />
                  )}
                </Card>
              </Col>
            </Row>
          </TabPane>
        </Tabs>
      </Card>

      {/* Approval Modal */}
      <Modal
        title={`Approve Match: ${approvalModal.item?.original_name}`}
        open={approvalModal.visible}
        onCancel={() => setApprovalModal({ visible: false, item: null })}
        footer={null}
        width={600}
      >
        <Form layout="vertical" onFinish={handleApprovePending}>
          <Form.Item
            name="vendor_id"
            label="Map to Vendor"
            rules={[{ required: true, message: 'Please select a vendor' }]}
            initialValue={approvalModal.item?.suggestions?.[0]?.vendor_id}
          >
            <Select
              showSearch
              placeholder="Search vendors..."
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {vendors.map((v) => (
                <Select.Option key={v.id} value={v.id}>
                  {v.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {approvalModal.item?.suggestions?.length > 0 && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Text strong>Suggestions:</Text>
              {approvalModal.item.suggestions.slice(0, 3).map((s, i) => (
                <div key={i} style={{ marginTop: 8 }}>
                  <Button
                    size="small"
                    type="link"
                    onClick={() => {
                      // Set the vendor_id in form
                    }}
                  >
                    {vendors.find(v => v.id === s.vendor_id)?.name || `Vendor #${s.vendor_id}`}
                  </Button>
                  <Tag color={s.confidence === 'high' ? 'green' : 'orange'}>
                    {s.combined_score?.toFixed(1)}%
                  </Tag>
                </div>
              ))}
            </Card>
          )}

          <Form.Item
            name="create_global_alias"
            label="Create Global Alias"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
          <Text type="secondary" style={{ display: 'block', marginTop: -16, marginBottom: 16 }}>
            Creating a global alias will help match this name automatically for all future imports across all organizations.
          </Text>

          <Form.Item name="notes" label="Notes (optional)">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Approve Match
              </Button>
              <Button onClick={() => setApprovalModal({ visible: false, item: null })}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Create/Edit Alias Modal */}
      <Modal
        title={aliasModal.alias ? 'Edit Global Alias' : 'Create Global Alias'}
        open={aliasModal.visible}
        onCancel={() => setAliasModal({ visible: false, alias: null })}
        footer={null}
        destroyOnClose
      >
        <Form
          layout="vertical"
          onFinish={handleAliasSubmit}
          initialValues={aliasModal.alias ? {
            alias_name: aliasModal.alias.alias_name,
            vendor_id: aliasModal.alias.vendor_id,
            alias_type: aliasModal.alias.alias_type || 'general',
            status: aliasModal.alias.status || 'active',
          } : { alias_type: 'general', status: 'active' }}
          key={aliasModal.alias?.id || 'new'}
        >
          <Form.Item
            name="alias_name"
            label="Alias Name"
            rules={[{ required: !aliasModal.alias, message: 'Please enter alias name' }]}
          >
            <Input
              placeholder="e.g., MSFT, Microsoft Corp"
              disabled={!!aliasModal.alias}
            />
          </Form.Item>
          {aliasModal.alias && (
            <Text type="secondary" style={{ display: 'block', marginTop: -16, marginBottom: 16 }}>
              Alias name cannot be changed. Delete and create a new alias if needed.
            </Text>
          )}

          <Form.Item
            name="vendor_id"
            label="Map to Vendor"
            rules={[{ required: !aliasModal.alias, message: 'Please select a vendor' }]}
          >
            <Select
              showSearch
              placeholder="Search vendors..."
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
              disabled={!!aliasModal.alias}
            >
              {vendors.map((v) => (
                <Select.Option key={v.id} value={v.id}>
                  {v.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {aliasModal.alias && (
            <Text type="secondary" style={{ display: 'block', marginTop: -16, marginBottom: 16 }}>
              Vendor mapping cannot be changed. Delete and create a new alias if needed.
            </Text>
          )}

          <Form.Item name="alias_type" label="Alias Type">
            <Select>
              <Select.Option value="general">General</Select.Option>
              <Select.Option value="abbreviation">Abbreviation</Select.Option>
              <Select.Option value="former_name">Former Name</Select.Option>
              <Select.Option value="dba">DBA (Doing Business As)</Select.Option>
              <Select.Option value="legal_name">Legal Name</Select.Option>
              <Select.Option value="typo">Common Typo</Select.Option>
            </Select>
          </Form.Item>

          {aliasModal.alias && (
            <Form.Item name="status" label="Status">
              <Select>
                <Select.Option value="active">Active</Select.Option>
                <Select.Option value="inactive">Inactive</Select.Option>
                <Select.Option value="deprecated">Deprecated</Select.Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {aliasModal.alias ? 'Update Alias' : 'Create Alias'}
              </Button>
              <Button onClick={() => setAliasModal({ visible: false, alias: null })}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VendorMapping;
